import { useEffect } from 'react';
import {
  PORTFOLIO_ITEMS,
  TELUGU_FEATURE_FILMS,
  SHOWREEL_URL,
  SHOWREEL_POSTER_URL,
  RUDRA_PREVIEW_URL,
} from '../data/portfolioData';

export const SEO_FAQS = [
  {
    question: 'How can D2C brands turn product photos into luxury jewellery, footwear, or fashion video ads without a physical shoot?',
    answer:
      'Using our 100% AI-Native product pipeline, you send product photos or CAD references and we generate macro diamond jewellery reels, embroidered bridal sneaker spots (Vendoz), leather boot commercials (Zoom Shoes), designer handbag lookbooks (MHPU), and liquor bottle commercials (Alcobrew BHRUM) with realistic physics, reflections, and on-model styling — ready for Meta, TikTok, Amazon, and Shopify.',
  },
  {
    question: 'Do you offer white-label AI video production and AI UGC retainers for marketing and ad agencies?',
    answer:
      'Yes. We operate as a white-label AI video production and AI UGC partner for creative, performance, and media agencies in the US, UK, Dubai/UAE, Europe, and India. We deliver unbranded 16:9 and 9:16 master files, multi-hook A/B testing batches, and creator-style AI spokesperson ads under strict NDA so your agency owns the client relationship.',
  },
  {
    question: 'How do you maintain character consistency across multi-shot commercials and 60–100 episode vertical micro-dramas?',
    answer:
      'We lock character facial identity, wardrobe, and spatial lighting using multi-keyframe reference conditioning across Seedance 2.0, Kling, Veo 3, Runway Gen-4, and custom ComfyUI node graphs. This allows the exact same protagonist to perform across entire 9:16 micro-drama series for DramaBox and Dashverse without facial drift.',
  },
  {
    question: 'Can you replace green screens with 40,000-seat stadiums or relight flat location footage to golden hour?',
    answer:
      'Yes. Through our Hybrid Live-Action + AI VFX pipeline, we transform studio green-screen and blue-screen plates into 40,000-seat floodlit stadiums, sunlit living rooms, or automated bottling plants, and use Beeble SwitchX 4K PBR relighting to turn flat overcast exterior shoots into golden-hour mountain valleys.',
  },
  {
    question: 'Do you provide AI previsualization (AI previs) and pitch trailers for film directors and OTT greenlights?',
    answer:
      'Yes. Directors, producers, and studios hire us to produce AI previsualization (AI previs) animatics, storyboard sequences, and proof-of-concept pitch trailers with synchronized sound and locked character blocking before committing to multi-million-dollar principal photography.',
  },
  {
    question: 'Which AI VFX studio in Hyderabad and India works on theatrical Telugu (Tollywood) feature films?',
    answer:
      'Headquartered in Hyderabad, MotionX Studios delivers Hybrid AI VFX, crowd synthesis, neural relighting, and 2K/4K ProRes 4444 compositing for Telugu theatrical feature films including #CULT (Vishwak Sen), HONEY (Naveen Chandra), MANODU G.O.A.T (Sudigali Sudheer), and our 90+ minute hybrid feature film RUDRA.',
  },
  {
    question: 'Can AI VFX handle realistic facial age-progression or de-aging for commercials and films?',
    answer:
      'Yes. As demonstrated in our BKIC Insurance Ramadan campaign, our Hybrid AI character pipeline performs frame-consistent facial age-progression and de-aging across decades while preserving the actor’s exact micro-expressions, eye line, glasses reflections, and on-set lighting.',
  },
  {
    question: 'Do you produce Ramadan campaigns, real estate brand films, and automotive commercials for Dubai, UAE, and GCC brands?',
    answer:
      'Yes. We have delivered broadcast Ramadan campaigns (BKIC Insurance), luxury waterfront real estate films (Seef Properties), and electric SUV automotive commercials (XPENG G9) across Bahrain, Dubai, the UAE, and the wider MENA region.',
  },
  {
    question: 'How much does it cost to hire an AI video studio and what is the turnaround time?',
    answer:
      'Our AI-Native and Hybrid VFX pipelines cut traditional production and CGI costs by 60–80% and compress 8–12 week schedules into 1–3 weeks for broadcast commercials and 3–5 days for D2C AI UGC and e-commerce product video batches.',
  },
  {
    question: 'Are AI-generated commercials, product videos, and hybrid VFX shots copyright-safe for TV and paid social?',
    answer:
      'Yes. Every production is human-directed, artist-composited in DaVinci Resolve, Nuke, and Premiere Pro, protected under enterprise NDA, and delivered with 100% client-owned commercial IP rights for broadcast TV, theatrical DCP, and paid social.',
  },
];

export default function SEOHead({
  title = 'AI Video Production Company, AI UGC Agency & Hybrid VFX Studio | MotionX Studios',
  description = 'Full-service AI video production company, done-for-you AI UGC agency for D2C & e-commerce brands, 9:16 vertical micro-drama studio (DramaBox, Dashverse), and Hybrid AI VFX studio for feature films (#CULT, HONEY, MANODU G.O.A.T) & global commercials.',
  canonicalPath = '/',
  filterPillar = 'all',
}) {
  useEffect(() => {
    document.title = title;

    const setMeta = (selector, attr, val) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        if (selector.includes('property=')) {
          el.setAttribute('property', selector.match(/property="([^"]+)"/)[1]);
        } else if (selector.includes('name=')) {
          el.setAttribute('name', selector.match(/name="([^"]+)"/)[1]);
        }
        document.head.appendChild(el);
      }
      el.setAttribute(attr, val);
    };

    setMeta('meta[name="description"]', 'content', description);
    setMeta('meta[property="og:title"]', 'content', title);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:url"]', 'content', `https://www.motionx.in${canonicalPath}`);
    setMeta('meta[name="twitter:title"]', 'content', title);
    setMeta('meta[name="twitter:description"]', 'content', description);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `https://www.motionx.in${canonicalPath}`);

    const items =
      filterPillar === 'all'
        ? PORTFOLIO_ITEMS
        : PORTFOLIO_ITEMS.filter((x) =>
            filterPillar === 'vertical' ? x.aspect === '9:16' : x.category === filterPillar
          );

    const creatorOrg = {
      '@type': 'Organization',
      name: 'MotionX Studios',
      url: 'https://www.motionx.in',
    };

    const publisherOrg = {
      '@type': 'Organization',
      name: 'MotionX Studios',
      url: 'https://www.motionx.in',
      logo: {
        '@type': 'ImageObject',
        url: 'https://www.motionx.in/logo192.png',
      },
    };

    const videoObjects = [
      {
        '@type': 'VideoObject',
        position: 1,
        name: 'AI Video Production Company & Hybrid VFX Showreel 2026 | MotionX Studios',
        description:
          'Official showreel from MotionX Studios showcasing 100% AI-Native commercials, D2C AI UGC product ads, 9:16 vertical micro-dramas, Originals, music videos, and Hybrid Live-Action + AI VFX for feature films.',
        thumbnailUrl: [SHOWREEL_POSTER_URL],
        contentUrl: SHOWREEL_URL,
        embedUrl: 'https://www.motionx.in/',
        uploadDate: '2026-05-01T00:00:00+05:30',
        duration: 'PT1M28S',
        creator: creatorOrg,
        publisher: publisherOrg,
      },
      {
        '@type': 'VideoObject',
        position: 2,
        name: 'RUDRA — Hybrid AI Feature Film Preview | MotionX Studios',
        description:
          'Flagship 90+ minute hybrid AI feature film shot with real actors and transformed through the MotionX AI VFX pipeline.',
        thumbnailUrl: [SHOWREEL_POSTER_URL],
        contentUrl: RUDRA_PREVIEW_URL,
        embedUrl: 'https://www.motionx.in/#feature-films',
        uploadDate: '2026-05-01T00:00:00+05:30',
        duration: 'PT1M30S',
        creator: creatorOrg,
        publisher: publisherOrg,
      },
      ...items.map((item, idx) => ({
        '@type': 'VideoObject',
        position: idx + 3,
        name: `${item.title} — ${item.subtitle} | MotionX Studios`,
        description: item.description,
        thumbnailUrl: [item.posterUrl],
        contentUrl: item.videoUrl,
        embedUrl: `https://www.motionx.in${canonicalPath}`,
        uploadDate: '2026-05-01T00:00:00+05:30',
        duration: item.isoDuration || 'PT45S',
        creator: creatorOrg,
        publisher: publisherOrg,
      })),
    ];

    const movieObjects = TELUGU_FEATURE_FILMS.map((m) => ({
      '@type': 'Movie',
      name: m.title,
      image: m.posterUrl,
      description: m.description,
      director: { '@type': 'Person', name: m.director },
      productionCompany: creatorOrg,
    }));

    const jsonLd = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': ['Organization', 'VideoProductionCompany', 'ProfessionalService'],
          '@id': 'https://www.motionx.in/#organization',
          name: 'MotionX Studios',
          alternateName: [
            'MotionX',
            'MotionX AI Video Production Company',
            'MotionX AI UGC Agency',
            'MotionX AI VFX Studio',
          ],
          url: 'https://www.motionx.in',
          logo: 'https://www.motionx.in/logo512.png',
          image: 'https://www.motionx.in/og-share-image.jpg',
          description,
          slogan: 'Impossible worlds. Real emotion. 100% AI-Native Production & Hybrid Live-Action + AI VFX.',
          foundingDate: '2025',
          founder: { '@type': 'Person', name: 'Jagan', url: 'https://www.motionx.in' },
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Hyderabad',
            addressRegion: 'Telangana',
            addressCountry: 'IN',
          },
          areaServed: [
            'United States',
            'United Kingdom',
            'United Arab Emirates',
            'Bahrain',
            'Europe',
            'India',
            'Worldwide',
          ],
          knowsAbout: [
            'AI Video Production Company',
            'AI Commercial Production Agency',
            'AI UGC Agency for DTC & E-Commerce Brands',
            'D2C E-Commerce Product Video Production',
            '9:16 Vertical Micro-Drama Production Company',
            'Hybrid Live-Action + AI VFX Studio',
            'Feature Film & Telugu Movie VFX Studio',
            'Green Screen AI Set Extension & Neural Relighting',
            'AI Music Video & Original Film Production',
            'Seedance 2.0, Runway, Kling, Veo 3, Beeble SwitchX & ComfyUI Pipelines',
          ],
          hasOfferCatalog: {
            '@type': 'OfferCatalog',
            name: 'AI Video Production, AI UGC, Micro-Drama & Hybrid VFX Services',
            itemListElement: [
              {
                '@type': 'Offer',
                itemOffered: {
                  '@type': 'Service',
                  name: 'AI Commercial Production & Brand Films',
                  url: 'https://www.motionx.in/ai-commercials-ugc',
                  description: 'Broadcast commercials, TVCs, and digital brand films produced 100% with AI or via Hybrid Live-Action + AI VFX.',
                },
              },
              {
                '@type': 'Offer',
                itemOffered: {
                  '@type': 'Service',
                  name: 'Done-For-You AI UGC Ads & D2C E-Commerce Product Videos',
                  url: 'https://www.motionx.in/ai-ugc-agency',
                  description: 'Creator-style 9:16 AI UGC video ads and photoreal D2C product commercials for footwear, jewellery, fashion, and fintech brands on Meta, TikTok, YouTube, Amazon, and Shopify.',
                },
              },
              {
                '@type': 'Offer',
                itemOffered: {
                  '@type': 'Service',
                  name: '9:16 Vertical Micro-Drama & Episodic Series Production',
                  url: 'https://www.motionx.in/micro-drama-production',
                  description: 'End-to-end 9:16 vertical drama series produced for mobile streaming platforms like DramaBox, ReelShort, and Dashverse.',
                },
              },
              {
                '@type': 'Offer',
                itemOffered: {
                  '@type': 'Service',
                  name: 'Hybrid AI VFX for Feature Films & Commercials',
                  url: 'https://www.motionx.in/ai-vfx',
                  description: 'Green-screen replacement, AI set & stadium crowd extension, Beeble SwitchX relighting, and 2K/4K ProRes 4444 compositing for theatrical films and commercials.',
                },
              },
              {
                '@type': 'Offer',
                itemOffered: {
                  '@type': 'Service',
                  name: 'AI Filmmaking, Short Films & Official Music Videos',
                  url: 'https://www.motionx.in/ai-filmmaking',
                  description: 'Original AI cinema, pitch trailers, previsualization, and 4K official music videos.',
                },
              },
            ],
          },
        },
        ...movieObjects,
        {
          '@type': 'ItemList',
          name: 'MotionX Studios Video Production Portfolio (28 Films & Ads)',
          numberOfItems: videoObjects.length,
          itemListElement: videoObjects,
        },
        {
          '@type': 'FAQPage',
          mainEntity: SEO_FAQS.map((f) => ({
            '@type': 'Question',
            name: f.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: f.answer,
            },
          })),
        },
      ],
    };

    let scriptEl = document.getElementById('mx-dynamic-jsonld');
    if (!scriptEl) {
      scriptEl = document.createElement('script');
      scriptEl.id = 'mx-dynamic-jsonld';
      scriptEl.type = 'application/ld+json';
      document.head.appendChild(scriptEl);
    }
    scriptEl.textContent = JSON.stringify(jsonLd);
  }, [title, description, canonicalPath, filterPillar]);

  return null;
}
